import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { localSort } from 'app/shared/lib/sorting';
import { Api } from 'app/shared/lib/essette-api';
import { guid } from 'app/shared/lib/guid';

interface IBrick {
    id: number;
    description: string;
}

@autoinject()
export class DemoEssPagerPage extends ComponentBase {
    ////****************** Fields and Properties ************************************////
    protected allBricks: IBrick[] = [];
    protected displayedBricks: IBrick[] = [];
    protected id: guid = guid();

    @bindable.numberNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected resultCount = 30;

    @bindable.numberNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected pageSize = 10;

    protected sorting = new Sorting();
    protected defaultSorting = new Sorting();

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected readonly api: Api,
        ...rest: any[]) {
        super(...rest);

        this.defaultSorting.entries = [
            {
                reference: 'id',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        // guard against invalid user input
        if(this.resultCount > 30) this.resultCount = 30;
        if(this.resultCount < 0 || !this.resultCount) this.resultCount = 0;
        if(this.pageSize <= 0 || !this.pageSize) this.pageSize = 1;
        // manually set page to 0 to accomodate the way this demo handles its table data
        this.$pager.updatePage(0);
        await this.loadBricks();
    }

    protected updateDisplayedBricks(): void {
        // display only the results appropriate to the page and page size
        this.displayedBricks = this.allBricks.slice(this.$pager.page * this.pageSize, this.$pager.page * this.pageSize + this.pageSize);
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        // if no sorting entries specified, use default sorting
        localSort(this.allBricks, sorting.entries.length > 0 ? this.sorting : this.defaultSorting);
        this.updateDisplayedBricks();
    }

    protected async loadBricks(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.api.mockGet('bricks');
            this.allBricks = response.results.slice(0, this.resultCount);
            this.updateDisplayedBricks();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
